/**
 * React hook for uploading images to an organization via POST (multipart/form-data).
 *
 * This hook sends files along with optional tags/context/metadata and directory.
 * It surfaces success/error via React Query and retries only on network/5xx errors.
 *
 * Usage:
 * const { mutateAsync: uploadImages, data, isPending, error, reset } =
 *   useUploadImagesToOrgMutation();
 *
 * await uploadImages({
 *   orgId,
 *   files, // File[] or FileList
 *   directory: 'events/2025-10-03',
 *   tags: [['hero', 'homepage'], ['menu']],
 *   context: [{ usage: 'banner' }],
 *   metadata: [{ alt: 'Sushi Night', categories: ['food', 'event'] }]
 * });
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import type { AssetTagValue } from '@Types/assets';
import service from '../service';
import { assetsKeys } from './useAssets';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';

/** Maximum file size in bytes (10MB) */
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

/** Format bytes to human-readable string */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface UploadToOrgInput {
  orgId: string;
  /** Files from input or drag-drop */
  files: File[] | FileList;
  /** Optional directory/prefix on server */
  directory?: string;
  /** These are JSON.stringified before sending */
  tags?: string[][];
  context?: Array<Record<string, string>>;
  metadata?: Array<Record<string, string | string[]>>;
  /** Asset tags to associate with all uploaded images */
  assetTags?: AssetTagValue[];
  /** Optional AbortSignal passthrough */
  signal?: AbortSignal;
}

export type UploadToOrgResult = Awaited<
  ReturnType<typeof service.uploadImagesToOrg>
>;

/** Internal: build FormData exactly as your controller + multer expect. */
function buildUploadFormData(input: UploadToOrgInput): FormData {
  const { files, directory, tags, context, metadata, assetTags } = input;

  const list = Array.from(files as File[] | FileList);
  if (list.length === 0) throw new Error('No files were attached.');
  if (list.length > 24)
    throw new Error('You can upload up to 24 files at once.');

  // Validate file sizes
  const oversizedFiles: string[] = [];
  for (const file of list) {
    if (file.size > MAX_FILE_SIZE) {
      oversizedFiles.push(
        `${file.name} (${formatFileSize(file.size)} exceeds ${formatFileSize(MAX_FILE_SIZE)})`,
      );
    }
  }

  if (oversizedFiles.length > 0) {
    throw new Error(
      `File size exceeds the maximum allowed size of ${formatFileSize(MAX_FILE_SIZE)}: ${oversizedFiles.join(', ')}`,
    );
  }

  const fd = new FormData();

  // IMPORTANT: field name must match multer.array('images', 24)
  for (const file of list) {
    fd.append('images', file, file.name);
  }

  if (directory) fd.append('directory', directory);
  if (tags) fd.append('tags', JSON.stringify(tags));
  if (context) fd.append('context', JSON.stringify(context));
  if (metadata) fd.append('metadata', JSON.stringify(metadata));
  if (assetTags && assetTags.length > 0) fd.append('assetTags', JSON.stringify(assetTags));

  return fd;
}

/**
 * Upload images to an org (multipart/form-data).
 *
 * @param options Optional react-query mutation options
 * @returns React Query mutation result for uploading images
 */
export function useUploadImagesToOrgMutation(
  options?: UseMutationOptions<UploadToOrgResult, Error, UploadToOrgInput>,
): UseMutationResult<UploadToOrgResult, Error, UploadToOrgInput> {
  const queryClient = useQueryClient();

  return useMutation<UploadToOrgResult, Error, UploadToOrgInput>({
    // You can include orgId in the key if you usually upload per-org
    mutationKey: ['org-images', 'upload'],
    mutationFn: async (input) => {
      const { orgId, signal } = input;
      const formData = buildUploadFormData(input);

      // Do NOT set Content-Type; browser sets multipart boundary.
      const res = await service.uploadImagesToOrg(orgId, formData, { signal });

      if (!res) throw new Error('Image upload failed: empty response.');

      const r = res as any;
      if (r?.error || r?.message === 'error') {
        throw new Error(
          r?.error?.message ?? r?.message ?? 'Image upload failed.',
        );
      }

      return res;
    },
    onSuccess: (_newAsset) => {
      // Easiest: invalidate all asset lists
      queryClient.invalidateQueries({ queryKey: assetsKeys.all });
      toastSuccess('Image(s) uploaded successfully.');
    },
    // Retry only on network/5xx; never on 4xx or app-level errors we throw above.
    retry: (failureCount, error: any) => {
      const status =
        error?.status ??
        error?.response?.status ??
        error?.cause?.status ??
        null;
      const isServerOrNetwork = status == null || status >= 500;
      return failureCount < 2 && isServerOrNetwork;
    },
    ...options,
  });
}
